<template>
    <div class="stories-in-search-wrapper">
        <div v-if="searchStories === null" class="stories-in-search-row">
            <div v-for="n in placeholderStoriesCount" :key="'skeleton-' + n" class="stories-in-search-item">
                <div class="stories-in-search-circle stories-in-search-circle--skeleton"></div>
                <div class="stories-in-search-placeholder-name"></div>
            </div>
        </div>
        <div v-else class="stories-in-search-row" ref="storiesRow">
            <div v-if="isEscort"
                 class="stories-in-search-item"
                 @click="goToCreateStory">
                <div class="stories-in-search-circle stories-in-search-circle--your-story">
                    <img :src="userFirstPhoto"
                         :alt="translate.account.yourStory"
                         class="stories-in-search-avatar"/>
                    <span class="stories-in-search-plus">+</span>
                </div>
                <span class="stories-in-search-username">{{ translate.account.yourStory }}</span>
            </div>

            <div v-for="(story, index) in sortedStories"
                 :key="story.id"
                 class="stories-in-search-item"
                 @click="onStoryClick(story, index)">
                <div class="stories-in-search-circle"
                     :class="{'stories-in-search-circle--seen': story.seen}">
                    <img :src="getStoryThumbnail(story)"
                         :alt="story.name"
                         class="stories-in-search-avatar"/>
                </div>
                <span class="stories-in-search-username">{{ story.name }}</span>
            </div>
        </div>

        <StoryViewerComponent
            v-if="viewerOpen"
            :stories="viewerStories"
            :initial-story-index="viewerStartIndex"
            @close="closeViewer"
            @story-seen="markStorySeen"
        />

        <div v-if="showExplicitModal"
             class="stories-explicit-modal-overlay"
             @click="closeExplicitModal">
            <div class="stories-explicit-modal-content" @click.stop>
                <ExplicitPhotoPlaceholderComponent
                    :profile-user-id="explicitStoryUserId"
                    verification-origin="search"
                />
                <button class="stories-explicit-modal-close" @click="closeExplicitModal">
                    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {mapState} from 'vuex';
import userMixin from '@/components/mixins/userMixin';
import ageVerificationMixin from '@/components/mixins/ageVerificationMixin';
import localStorageMixin from '@/components/mixins/localStorageMixin';
import photoMixin from '@/components/mixins/photoMixin';
import StoryViewerComponent from './StoryViewerComponent';
import ExplicitPhotoPlaceholderComponent from '@/components/partials/ExplicitPhotoPlaceholderComponent';
import {NuxtRoutes} from '@/core/constants/v2/nuxtRoutes';
import {regionRelativeUrl} from '@/core/utils/helpers/regions/regionUrlHelper';
import constants from '@/core/constants';
import {LocalStorageKeysEnum} from '@/core/constants/enums/localStorage/localStorageKeysEnum';

const data = () => {
    return {
        placeholderStoriesCount: 20,
        viewerOpen: false,
        viewerStartIndex: 0,
        viewerStories: [],
        seenStoryIds: [],
        isDragging: false,
        dragStartX: 0,
        scrollStartX: 0,
        hasDragMoved: false,
        dragEndTimer: null,
        pendingStoryId: null,
        showExplicitModal: false,
        explicitStoryUserId: null,
    };
};

function mounted() {
    const seenStoriesIdsFromLocalStorage = this.getLocalStorageItem(LocalStorageKeysEnum.STORIES_SEEN_IDS);
    this.seenStoryIds = seenStoriesIdsFromLocalStorage ? JSON.parse(seenStoriesIdsFromLocalStorage) : [];
}

function beforeDestroy() {
    clearTimeout(this.dragEndTimer);
    this.destroyDragScroll();
    this.$store.commit('setSearchStories', null);
}

function buildStoryItems(story, canView) {
    if (!canView) {
        return [{
            id: `story${story.id}_placeholder`,
            type: 'photo',
            src: constants.user_photo.EXPLICIT_STORY_PLACEHOLDER,
            thumbnail: constants.user_photo.EXPLICIT_STORY_PLACEHOLDER,
            duration: 3,
            width: 0,
            height: 0,
            isAdaptive: false,
        }];
    }

    const media = _.get(story, 'media', []);
    if (!media.length) {
        return [];
    }

    return media.map((item, index) => {
        if (item.type === 'photo') {
            const imageSrc = _.get(item, 'imageUrl', '');

            return {
                id: `story${story.id}_photo${item.id || index}_${index}`,
                type: 'photo',
                src: imageSrc,
                thumbnail: imageSrc,
                duration: 10,
                width: item.width,
                height: item.height,
                isAdaptive: false,
            };
        }

        return {
            id: `story${story.id}_video${item.id || index}_${index}`,
            type: 'video',
            src: item.videoUrl || '',
            thumbnail: item.thumbnailUrl || '',
            duration: item.duration || 15,
            width: item.width,
            height: item.height,
            isAdaptive: item.videoUrl ? item.videoUrl.includes('.m3u8') : false,
        };
    });
}

function firstMediaThumbnail(story) {
    const firstMedia = _.get(story, 'media[0]');
    if (!firstMedia) {
        return '';
    }

    if (firstMedia.type === 'photo') {
        return _.get(firstMedia, 'imageUrl', '');
    }

    return _.get(firstMedia, 'thumbnailUrl', '');
}

function getStoryThumbnail(story) {
    if (story.isExplicit && !this.canViewExplicit) {
        return constants.user_photo.EXPLICIT_STORY_AVATAR_PLACEHOLDER;
    }
    return story.photo || constants.user_photo.NO_PHOTO_SEARCH_URL_SMALL;
}

function onStoryClick(story, index) {
    if (this.hasDragMoved) {
        return;
    }

    if (story.isExplicit && !this.canViewExplicit) {
        if (!this.isLoggedIn) {
            this.pendingStoryId = story.storyId;
            $('#modalNotLoggedIn').modal('show');
            return;
        }

        if (this.isPunter) {
            this.explicitStoryUserId = story.userId;
            this.pendingStoryId = story.storyId;
            this.showExplicitModal = true;
        }

        return;
    }

    this.viewerStories = [...this.sortedStories];
    this.viewerStartIndex = index;
    this.viewerOpen = true;
}

function closeExplicitModal() {
    this.showExplicitModal = false;
    this.explicitStoryUserId = null;
    this.pendingStoryId = null;
}

function goToCreateStory() {
    window.location.href = regionRelativeUrl(NuxtRoutes.USERS.STORIES_CREATE);
}

function openPendingStory() {
    const storyId = this.pendingStoryId;
    this.pendingStoryId = null;

    const index = this.sortedStories.findIndex(s => s.storyId === storyId);
    if (index === -1) {
        return;
    }

    this.viewerStories = [...this.sortedStories];
    this.viewerStartIndex = index;
    this.viewerOpen = true;
}

function closeViewer() {
    this.viewerOpen = false;
    this.viewerStories = [];
}

function markStorySeen(storyId) {
    if (!this.seenStoryIds.includes(storyId)) {
        this.seenStoryIds.push(storyId);
        this.setLocalStorageItem(LocalStorageKeysEnum.STORIES_SEEN_IDS, JSON.stringify(this.seenStoryIds));
    }
}

function initDragScroll() {
    if ('ontouchstart' in window) {
        return;
    }

    const el = this.$refs.storiesRow;
    if (!el) {
        return;
    }

    el.addEventListener('mousedown', this.onDragStart);
    el.addEventListener('mousemove', this.onDragMove);
    el.addEventListener('mouseup', this.onDragEnd);
    el.addEventListener('mouseleave', this.onDragEnd);
    el.addEventListener('click', this.onDragClick, true);
}

function destroyDragScroll() {
    const el = this.$refs.storiesRow;
    if (!el) {
        return;
    }

    el.removeEventListener('mousedown', this.onDragStart);
    el.removeEventListener('mousemove', this.onDragMove);
    el.removeEventListener('mouseup', this.onDragEnd);
    el.removeEventListener('mouseleave', this.onDragEnd);
    el.removeEventListener('click', this.onDragClick, true);
}

function onDragClick(event) {
    if (this.hasDragMoved) {
        event.preventDefault();
        event.stopPropagation();
    }
}

function onDragStart(event) {
    this.isDragging = true;
    this.hasDragMoved = false;
    this.dragStartX = event.pageX - this.$refs.storiesRow.offsetLeft;
    this.scrollStartX = this.$refs.storiesRow.scrollLeft;
    event.preventDefault();
}

function onDragMove(event) {
    if (!this.isDragging) {
        return;
    }

    const cursorPositionX = event.pageX - this.$refs.storiesRow.offsetLeft;
    if (Math.abs(cursorPositionX - this.dragStartX) > 5) {
        this.hasDragMoved = true;
    }

    const scrollDistance = (cursorPositionX - this.dragStartX) * 2;
    this.$refs.storiesRow.scrollLeft = this.scrollStartX - scrollDistance;
}

function onDragEnd() {
    this.isDragging = false;
    clearTimeout(this.dragEndTimer);
    this.dragEndTimer = setTimeout(() => {
        this.hasDragMoved = false;
    }, 10);
}

export default {
    name: 'StoriesInSearchComponent',
    mixins: [userMixin, ageVerificationMixin, photoMixin, localStorageMixin],
    components: {
        StoryViewerComponent,
        ExplicitPhotoPlaceholderComponent,
    },
    data,
    mounted,
    beforeDestroy,
    watch: {
        canViewExplicit(canView) {
            if (this.showExplicitModal) {
                if (canView) {
                    this.closeExplicitModal();
                } else if (this.isLoggedIn && this.isEscort) {
                    this.closeExplicitModal();
                }
            }

            if (canView && this.pendingStoryId) {
                this.openPendingStory();
            }
        },
        searchStories() {
            this.$nextTick(() => {
                this.destroyDragScroll();
                this.initDragScroll();
            });
        },
    },
    computed: {
        ...mapState(['translate', 'searchStories']),
        stories() {
            if (!this.searchStories) {
                return [];
            }

            return this.searchStories.map(story => {
                const showPlaceholder = story.isExplicit && !this.canViewExplicit;
                return {
                    id: story.id,
                    storyId: story.id,
                    photo: showPlaceholder
                        ? constants.user_photo.EXPLICIT_STORY_AVATAR_PLACEHOLDER
                        : (story.userPhotoSmall || firstMediaThumbnail(story) || constants.user_photo.NO_PHOTO_SEARCH_URL_SMALL),
                    name: story.username,
                    profileUrl: story.profilePath ? regionRelativeUrl(story.profilePath) : null,
                    isExplicit: story.isExplicit,
                    isSystem: !!story.isSystem,
                    userId: story.userId,
                    items: buildStoryItems(story, !showPlaceholder),
                    seen: this.seenStoryIds.includes(story.id),
                };
            });
        },
        sortedStories() {
            return [...this.stories].sort((a, b) => {
                if (a.seen === b.seen) {
                    return 0;
                }

                return a.seen ? 1 : -1;
            });
        },
        canViewExplicit() {
            return this.isLoggedIn && this.isPunter && this.isAgeVerified;
        },
        userFirstPhoto() {
            if (!this.currentUser) {
                return constants.user_photo.NO_PHOTO_SEARCH_URL_SMALL;
            }

            return this.getPhotoUrl(this.currentUser, 'jpg', 'small');
        },
    },
    methods: {
        getStoryThumbnail,
        onStoryClick,
        closeExplicitModal,
        goToCreateStory,
        openPendingStory,
        closeViewer,
        markStorySeen,
        initDragScroll,
        destroyDragScroll,
        onDragStart,
        onDragMove,
        onDragEnd,
        onDragClick,
    },
};
</script>
